import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPrepStationApi,
  deletePrepStationApi,
  getPrepStationApi,
  getPrepStationsApi,
  updatePrepStationApi,
} from "../api/prepStationsApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { PrepStationId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreatePrepStation,
  RestUpdatePrepStation,
} from "@KassaroAB/shared/types/prepStation";

const prepStationsQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "prepStations");

const invalidatePrepStationQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({ queryKey: prepStationsQueryKey(scope) });
};

export const useGetPrepStationsQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: prepStationsQueryKey(scope),
    queryFn: () => getPrepStationsApi(scope!),
    enabled: !!scope,
  });
};

export const useGetPrepStationQuery = (
  scope: CatalogScope | null,
  prepStationId: PrepStationId | null | undefined,
) => {
  return useQuery({
    queryKey: [...prepStationsQueryKey(scope), prepStationId],
    queryFn: () => getPrepStationApi(scope!, prepStationId!),
    enabled: !!scope && !!prepStationId,
  });
};

export const useCreatePrepStationMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (prepStation: RestCreatePrepStation) =>
      createPrepStationApi(scope!, prepStation),
    onSuccess: () => {
      invalidatePrepStationQueries(queryClient, scope);
    },
  });
};

export const useUpdatePrepStationMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      prepStationId,
      prepStation,
    }: {
      prepStationId: PrepStationId;
      prepStation: RestUpdatePrepStation;
    }) => updatePrepStationApi(scope!, prepStationId, prepStation),
    onSuccess: () => {
      invalidatePrepStationQueries(queryClient, scope);
    },
  });
};

export const useDeletePrepStationMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (prepStationId: PrepStationId) =>
      deletePrepStationApi(scope!, prepStationId),
    onSuccess: () => {
      invalidatePrepStationQueries(queryClient, scope);
    },
  });
};
